import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { FaCookieBite } from 'react-icons/fa';

// Cookie notice for the global page at "/". Informational, not a consent wall:
// it never blocks the page. No backdrop, no focus trap, no scroll lock and no
// aria-modal - visitors can scroll, click a country card or ignore it entirely.
//
// It costs nothing at load: nothing renders on the server (so hydration and
// the static HTML are unchanged), and it only appears once the browser is idle,
// so it never competes with the hero for LCP. The parent positions it in a
// fixed stack, so showing it causes no layout shift.
//
// The "Got it" flag is a cookie, not localStorage: UserContext calls
// localStorage.clear() on every login-state check for logged-out visitors,
// which is exactly who sees "/" (same reason as preferredMarket in
// utils/markets.js).
const ACK_COOKIE = 'cookieNotice';

export const hasAcknowledgedCookies = () => {
  try {
    return typeof document !== 'undefined' && new RegExp(`(?:^|; )${ACK_COOKIE}=1`).test(document.cookie);
  } catch {
    return false;
  }
};

export const acknowledgeCookies = () => {
  try {
    if (typeof document === 'undefined') return;
    document.cookie = `${ACK_COOKIE}=1; path=/; max-age=31536000; SameSite=Lax`;
  } catch {}
};

export const CookieNotice = () => {
  // mounted: in the DOM; shown: faded in (one frame later, so the transition runs).
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (hasAcknowledgedCookies()) return;
    let frame;
    const reveal = () => {
      setMounted(true);
      frame = requestAnimationFrame(() => setShown(true));
    };
    const idle = globalThis.requestIdleCallback;
    const handle = idle ? idle(reveal, { timeout: 3000 }) : setTimeout(reveal, 1500);
    return () => {
      if (idle) globalThis.cancelIdleCallback(handle);
      else clearTimeout(handle);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  if (!mounted) return null;

  const dismiss = () => {
    acknowledgeCookies();
    setMounted(false);
  };

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      className={`pointer-events-auto flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-700 shadow-xl transition-[opacity,transform] duration-300 motion-reduce:transition-none ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
    >
      <FaCookieBite className="shrink-0 text-xl text-amber-500" aria-hidden="true" />
      <p className="flex-1 text-sm">
        We use cookies to remember your country and to understand how the site is used.{' '}
        <Link href="/privacy-policy#cookies" className="font-semibold text-orange-600 underline underline-offset-2 hover:text-orange-700">
          Learn more
        </Link>
      </p>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
      >
        Got it
      </button>
    </div>
  );
};
